import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import PasswordField from '../components/PasswordField';
import { BrandStripe } from '../components/BrandStripe';
import { Button, Heading, Text } from '../components/ui';
import './SignIn.css';

type Mode = 'password' | 'magic' | 'forgot';
type Status = 'idle' | 'sending' | 'sent' | 'reset-sent' | 'error' | 'not-invited';

export default function SignIn() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorText, setErrorText] = useState('');
  const navigate = useNavigate();
  const { isAuthed, loading } = useSession();

  if (loading) return null;
  if (isAuthed) {
    navigate('/', { replace: true });
    return null;
  }

  async function whitelistCheck(normalizedEmail: string): Promise<
    { kind: 'ok' } | { kind: 'not_invited' } | { kind: 'error'; detail?: string }
  > {
    try {
      const res = await fetch('/api/sign-in', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      if (res.status === 403) return { kind: 'not_invited' };
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return { kind: 'error', detail: body?.detail ?? body?.error ?? `HTTP ${res.status}` };
      }
      return { kind: 'ok' };
    } catch (err: any) {
      return { kind: 'error', detail: err?.message ?? 'Network error' };
    }
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    const normalized = email.trim().toLowerCase();
    setStatus('sending');
    setErrorText('');

    const gate = await whitelistCheck(normalized);
    if (gate.kind === 'not_invited') { setStatus('not-invited'); return; }
    if (gate.kind === 'error') {
      setErrorText(`Sign-in precheck failed: ${gate.detail ?? 'unknown error'}`);
      setStatus('error');
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email: normalized, password });
    if (error) {
      // If the user has never set a password (magic-link-only to date), the
      // password sign-in will error. Surface a helpful hint.
      setErrorText(
        error.message.toLowerCase().includes('invalid')
          ? "That email / password doesn't match. If you've only used one time links before, switch to the One Time Link tab, then set a password in Profile."
          : error.message,
      );
      setStatus('error');
      return;
    }
    navigate('/', { replace: true });
  }

  async function submitMagic(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    setStatus('sending');
    setErrorText('');

    const gate = await whitelistCheck(normalized);
    if (gate.kind === 'not_invited') { setStatus('not-invited'); return; }
    if (gate.kind === 'error') {
      setErrorText(`Sign-in precheck failed: ${gate.detail ?? 'unknown error'}`);
      setStatus('error');
      return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      setErrorText(error.message);
      setStatus('error');
      return;
    }
    setStatus('sent');
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    setStatus('sending');
    setErrorText('');

    // Same precheck as sign-in: avoid spamming unknown addresses and
    // give a clear "not invited" message instead of a silent no-op.
    const gate = await whitelistCheck(normalized);
    if (gate.kind === 'not_invited') { setStatus('not-invited'); return; }
    if (gate.kind === 'error') {
      setErrorText(`Reset precheck failed: ${gate.detail ?? 'unknown error'}`);
      setStatus('error');
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(normalized, {
      // ?next= is read by AuthCallback after the PKCE exchange so the user
      // lands on the set-password screen instead of the home feed.
      redirectTo: `${window.location.origin}/auth/callback?next=/reset-password`,
    });
    if (error) {
      setErrorText(error.message);
      setStatus('error');
      return;
    }
    setStatus('reset-sent');
  }

  return (
    <div data-ds className="signin">
      <BrandStripe className="signin__stripe" />

      <main className="signin__form-side">
        <div className="signin__inner">
        {status !== 'sent' && status !== 'reset-sent' && (
          <p className="kicker signin__eyebrow">Conversion Club</p>
        )}

        {status !== 'sent' && status !== 'reset-sent' && status !== 'not-invited' && (
          <>
            {mode !== 'forgot' && (
              <div className="signin__tabs" role="tablist" aria-label="Sign-in method">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'password'}
                  className={`signin__tab ${mode === 'password' ? 'signin__tab--active' : ''}`}
                  onClick={() => { setMode('password'); setStatus('idle'); setErrorText(''); }}
                >
                  Email + password
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'magic'}
                  className={`signin__tab ${mode === 'magic' ? 'signin__tab--active' : ''}`}
                  onClick={() => { setMode('magic'); setStatus('idle'); setErrorText(''); }}
                >
                  One Time Link
                </button>
              </div>
            )}

            {mode === 'password' ? (
              <form className="signin__form" onSubmit={submitPassword}>
                <label htmlFor="si-email" className="visually-hidden">Email address</label>
                <input
                  id="si-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label htmlFor="si-password" className="visually-hidden">Password</label>
                <PasswordField
                  id="si-password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  placeholder="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="accent"
                  fullWidth
                  className="signin__submit"
                  disabled={!email || !password || status === 'sending'}
                >
                  {status === 'sending' ? 'Signing in…' : 'Sign in'}
                </Button>
                {status === 'error' && (
                  <Text role="small" className="signin__status signin__status--error">{errorText}</Text>
                )}
                <p className="signin__forgot">
                  <button
                    type="button"
                    className="signin__linkbtn"
                    onClick={() => { setMode('forgot'); setPassword(''); setStatus('idle'); setErrorText(''); }}
                  >
                    Forgot password?
                  </button>
                </p>
              </form>
            ) : mode === 'magic' ? (
              <form className="signin__form" onSubmit={submitMagic}>
                <label htmlFor="si-email-m" className="visually-hidden">Email address</label>
                <input
                  id="si-email-m"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="accent"
                  fullWidth
                  className="signin__submit"
                  disabled={!email || status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send one time link'}
                </Button>
                {status === 'error' && (
                  <Text role="small" className="signin__status signin__status--error">{errorText}</Text>
                )}
              </form>
            ) : (
              <form className="signin__form" onSubmit={submitForgot}>
                <Text role="small" muted className="signin__forgot-lede">
                  Enter your email and we'll send you a link to set a new password.
                </Text>
                <label htmlFor="si-email-f" className="visually-hidden">Email address</label>
                <input
                  id="si-email-f"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="accent"
                  fullWidth
                  className="signin__submit"
                  disabled={!email || status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send reset link'}
                </Button>
                {status === 'error' && (
                  <Text role="small" className="signin__status signin__status--error">{errorText}</Text>
                )}
                <p className="signin__forgot">
                  <button
                    type="button"
                    className="signin__linkbtn"
                    onClick={() => { setMode('password'); setStatus('idle'); setErrorText(''); }}
                  >
                    ← Back to sign in
                  </button>
                </p>
              </form>
            )}

          </>
        )}

        {status === 'sent' && (
          <div className="signin__sent">
            <p className="kicker">Check your inbox</p>
            <p className="signin__sent-line">Click the link and you're in. The tab closes itself.</p>
          </div>
        )}

        {status === 'reset-sent' && (
          <div className="signin__sent">
            <p className="kicker">Check your inbox</p>
            <p className="signin__sent-line">A reset link is on its way. Click it to set a new password.</p>
          </div>
        )}

        {status === 'not-invited' && (
          <div className="signin__denied">
            <Heading level="h2">This email hasn't been invited yet.</Heading>
            <Text role="body" muted>
              If you were expecting access, write to <a href="mailto:hello@convertedcloset.com">hello@convertedcloset.com</a>.
              Otherwise, the Club opens publicly shortly.
            </Text>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
